import { c as _c } from "react/compiler-runtime";
import { serviceQueryKeys } from "../admin/service-query";
import { taskSettingsQueryKeys } from "../tasks/task-settings-query";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { Team, TeamCreate, TeamUpdate } from "@/types/team";
import { createTeam, deleteTeam, fetchTeams, updateTeam } from "./teams";
export const teamsQueryKeys = {
  all: ["teams"] as const,
  list: () => [...teamsQueryKeys.all, "list"] as const
};
export function useTeams(t0) {
  const $ = _c(5);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    enabled: t2
  } = t1;
  const enabled = t2 === undefined ? true : t2;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = teamsQueryKeys.list();
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== enabled) {
    t4 = {
      queryKey: t3,
      queryFn: fetchTeams,
      select: _temp,
      enabled
    };
    $[3] = enabled;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  return useQuery(t4);
}
function _temp(data) {
  return data ?? [];
}
export function useTeamMutations() {
  const $ = _c(30);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== queryClient) {
    t0 = () => {
      queryClient.invalidateQueries({
        queryKey: teamsQueryKeys.list()
      });
      queryClient.invalidateQueries({
        queryKey: serviceQueryKeys.all
      });
    };
    $[0] = queryClient;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const invalidateTeams = t0;
  let t1;
  let t2;
  if ($[2] !== queryClient) {
    t1 = async data_0 => {
      await queryClient.cancelQueries({
        queryKey: teamsQueryKeys.list()
      });
      const previousTeams = queryClient.getQueryData(teamsQueryKeys.list());
      queryClient.setQueryData(teamsQueryKeys.list(), old => [...(old || []), {
        id: `temp-${Date.now()}`,
        name: data_0.name,
        description: data_0.description,
        members: data_0.memberIds.map(_temp3),
        serviceIds: data_0.serviceIds,
        notificationDefaults: [],
        createdDate: new Date().toISOString()
      }]);
      return {
        previousTeams
      };
    };
    t2 = (_err, _variables, context) => {
      if (context) {
        queryClient.setQueryData(teamsQueryKeys.list(), context.previousTeams);
      }
    };
    $[2] = queryClient;
    $[3] = t1;
    $[4] = t2;
  } else {
    t1 = $[3];
    t2 = $[4];
  }
  let t3;
  if ($[5] !== invalidateTeams || $[6] !== t1 || $[7] !== t2) {
    t3 = {
      mutationFn: _temp2,
      onMutate: t1,
      onError: t2,
      onSettled: invalidateTeams
    };
    $[5] = invalidateTeams;
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const t4 = useMutation(t3);
  let t5;
  let t6;
  if ($[9] !== queryClient) {
    t5 = async t7 => {
      const {
        teamId: teamId_0,
        data: data_2
      } = t7;
      await queryClient.cancelQueries({
        queryKey: teamsQueryKeys.list()
      });
      const previousTeams_0 = queryClient.getQueryData(teamsQueryKeys.list());
      queryClient.setQueryData(teamsQueryKeys.list(), old_0 => (old_0 || []).map(t => t.id === teamId_0 ? {
        ...t,
        name: data_2.name ?? t.name,
        description: data_2.description ?? t.description,
        serviceIds: data_2.serviceIds ?? t.serviceIds,
        members: data_2.memberIds !== undefined ? data_2.memberIds.map(id_0 => t.members.find(m => m.id === id_0) ?? {
          id: id_0,
          firstName: ""
        }) : t.members,
        updatedDate: new Date().toISOString()
      } : t));
      return {
        previousTeams: previousTeams_0
      };
    };
    t6 = (_err_0, _variables_0, context_0) => {
      if (context_0) {
        queryClient.setQueryData(teamsQueryKeys.list(), context_0.previousTeams);
      }
    };
    $[9] = queryClient;
    $[10] = t5;
    $[11] = t6;
  } else {
    t5 = $[10];
    t6 = $[11];
  }
  let t7;
  if ($[12] !== invalidateTeams || $[13] !== t5 || $[14] !== t6) {
    t7 = {
      mutationFn: _temp4,
      onMutate: t5,
      onError: t6,
      onSettled: invalidateTeams
    };
    $[12] = invalidateTeams;
    $[13] = t5;
    $[14] = t6;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  const t8 = useMutation(t7);
  let t10;
  let t9;
  if ($[16] !== queryClient) {
    t9 = async teamId_2 => {
      await queryClient.cancelQueries({
        queryKey: teamsQueryKeys.list()
      });
      const previousTeams_1 = queryClient.getQueryData(teamsQueryKeys.list());
      queryClient.setQueryData(teamsQueryKeys.list(), old_1 => (old_1 || []).filter(t_0 => t_0.id !== teamId_2));
      return {
        previousTeams: previousTeams_1
      };
    };
    t10 = (_err_1, _variables_1, context_1) => {
      if (context_1) {
        queryClient.setQueryData(teamsQueryKeys.list(), context_1.previousTeams);
      }
    };
    $[16] = queryClient;
    $[17] = t10;
    $[18] = t9;
  } else {
    t10 = $[17];
    t9 = $[18];
  }
  let t11;
  if ($[19] !== invalidateTeams || $[20] !== queryClient) {
    t11 = () => {
      invalidateTeams();
      queryClient.invalidateQueries({
        queryKey: taskSettingsQueryKeys.alertConfigs()
      });
    };
    $[19] = invalidateTeams;
    $[20] = queryClient;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] !== t10 || $[23] !== t11 || $[24] !== t9) {
    t12 = {
      mutationFn: _temp5,
      onMutate: t9,
      onError: t10,
      onSettled: t11
    };
    $[22] = t10;
    $[23] = t11;
    $[24] = t9;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  const t13 = useMutation(t12);
  let t14;
  if ($[26] !== t13 || $[27] !== t4 || $[28] !== t8) {
    t14 = {
      create: t4,
      update: t8,
      delete: t13
    };
    $[26] = t13;
    $[27] = t4;
    $[28] = t8;
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  return t14;
}
function _temp5(teamId_1) {
  return deleteTeam(teamId_1);
}
function _temp4(t0) {
  const {
    teamId,
    data: data_1
  } = t0;
  return updateTeam(teamId, data_1);
}
function _temp3(id) {
  return {
    id,
    firstName: ""
  };
}
function _temp2(data) {
  return createTeam(data);
}